import { CommonModule } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import {
  CargaMasivaResumen,
  FilaResultado
} from 'src/app/domain/model/carga-masiva/carga-masiva';

export type CargaMasivaTab = 'todos' | 'exitosos' | 'errores';

export interface CargaMasivaResultadoTableConfig {
  responsive: {
    minWidth?: string;
    scrollHeight?: string;
  };
  a11y: {
    tableAriaLabel: string;
  };
  style: {
    tableClassName: string;
    striped?: boolean;
    gridlines?: boolean;
  };
}

export interface CargaMasivaResultadoLabels {
  total: string;
  exitosos: string;
  errores: string;
  principal: string;
  estadoExitoso: string;
  btnTodos: string;
  btnExitosos: string;
  btnErrores: string;
  tituloPrincipal: string;
}

@Component({
  selector: 'app-carga-masiva-resultado',
  standalone: true,
  imports: [CommonModule, ButtonModule, TableModule, TagModule],
  template: `
    <div class="mt-4">
      <div
        class="mb-4 p-4 rounded-lg"
        [class]="
          resumen()!.filasConError === 0
            ? 'bg-green-100 border border-green-200 text-black'
            : 'bg-yellow-100 border border-yellow-200 text-black'
        "
      >
        <div class="flex items-center gap-3 mb-3">
          <i
            [class]="
              resumen()!.filasConError === 0
                ? 'pi pi-check-circle text-green-600 text-2xl'
                : 'pi pi-exclamation-triangle text-yellow-600 text-2xl'
            "
          ></i>
          <h4 class="text-lg font-semibold m-0">{{ resumen()!.mensaje }}</h4>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center mt-4">
          <div class="p-3 bg-blue-500 rounded-lg shadow-sm">
            <p class="text-2xl font-bold text-gray-800">
              {{ resumen()!.totalFilas }}
            </p>
            <p class="text-sm text-gray-500">{{ labels().total }}</p>
          </div>
          <div class="p-3 bg-green-500 rounded-lg shadow-sm">
            <p class="text-2xl font-bold text-green-600">
              {{ resumen()!.filasExitosas }}
            </p>
            <p class="text-sm text-gray-500">{{ labels().exitosos }}</p>
          </div>
          <div class="p-3 bg-red-400 rounded-lg shadow-sm">
            <p class="text-2xl font-bold text-red-600">
              {{ resumen()!.filasConError }}
            </p>
            <p class="text-sm text-gray-500">{{ labels().errores }}</p>
          </div>
        </div>
      </div>

      <div class="flex flex-wrap gap-2 mb-2">
        <p-button
          [label]="labels().btnTodos + ' (' + resumen()!.detalles.length + ')'"
          [severity]="tab() === 'todos' ? 'primary' : 'secondary'"
          [outlined]="tab() !== 'todos'"
          size="small"
          (onClick)="setTab('todos')"
        ></p-button>
        <p-button
          [label]="labels().btnExitosos + ' (' + resumen()!.filasExitosas + ')'"
          [severity]="tab() === 'exitosos' ? 'success' : 'secondary'"
          [outlined]="tab() !== 'exitosos'"
          size="small"
          (onClick)="setTab('exitosos')"
        ></p-button>
        <p-button
          [label]="labels().btnErrores + ' (' + resumen()!.filasConError + ')'"
          [severity]="tab() === 'errores' ? 'danger' : 'secondary'"
          [outlined]="tab() !== 'errores'"
          size="small"
          (onClick)="setTab('errores')"
        ></p-button>
      </div>

      <div
        class="md:hidden space-y-3 p-3 border rounded border-gray-200 dark:border-gray-700"
      >
        @if (filasVisibles().length === 0) {
          <div class="text-center text-gray-500 py-4">No hay filas para mostrar</div>
        }
        @for (row of filasVisibles(); track row.numeroFila) {
          <div
            class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
          >
            <div class="mb-3 min-w-0">
              <div class="text-xs text-gray-500 dark:text-gray-400">
                {{ labels().tituloPrincipal }}
              </div>
              <div
                class="font-semibold text-gray-800 dark:text-gray-100 truncate"
                [attr.title]="tituloTabla(principalValue(row))"
              >
                {{ textoTabla(principalValue(row)) }}
              </div>
            </div>
            <div class="grid grid-cols-1 gap-2 text-sm">
              <div>
                <span class="text-gray-500 dark:text-gray-400">Fila:</span>
                <span class="text-gray-800 dark:text-gray-200">{{
                  textoTabla(row.numeroFila)
                }}</span>
              </div>
              <div>
                <span class="text-gray-500 dark:text-gray-400">Resultado:</span>
                @if (!row.exitoso) {
                  <span class="ml-1 text-red-600">{{ textoTabla(errorValue(row)) }}</span>
                } @else {
                  <span class="ml-1 text-green-600"
                    ><i class="pi pi-check mr-1"></i>{{ labels().estadoExitoso }}</span
                  >
                }
              </div>
            </div>
          </div>
        }
      </div>

      <div class="hidden md:block">
        <div
          role="region"
          [attr.aria-label]="tableConfig().a11y.tableAriaLabel"
          class="sr-only"
        ></div>
        <p-table
          [resizableColumns]="true"
          columnResizeMode="expand"
          [value]="filasVisibles()"
          [scrollable]="true"
          [responsiveLayout]="'scroll'"
          [tableStyle]="{ 'min-width': tableConfig().responsive.minWidth || '72rem' }"
          [scrollHeight]="tableConfig().responsive.scrollHeight || '250px'"
          [styleClass]="tableStyleClass()"
        >
          <ng-template pTemplate="header">
            <tr>
              <th pResizableColumn class="w-16">
                <span class="grid-header-text" title="Fila">Fila</span>
              </th>
              <th pResizableColumn class="w-32">
                <span class="grid-header-text" [attr.title]="labels().principal">
                  {{ labels().principal }}
                </span>
              </th>
              <th pResizableColumn>
                <span class="grid-header-text" title="Resultado">Resultado</span>
              </th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td class="font-medium">
                <span class="grid-cell-text" [attr.title]="tituloTabla(row.numeroFila)">
                  {{ textoTabla(row.numeroFila) }}
                </span>
              </td>
              <td>
                <span class="grid-cell-text" [attr.title]="tituloTabla(principalValue(row))">
                  {{ textoTabla(principalValue(row)) }}
                </span>
              </td>
              <td>
                @if (!row.exitoso) {
                  <span class="grid-cell-text text-red-600" [attr.title]="tituloTabla(errorValue(row))">
                    {{ textoTabla(errorValue(row)) }}
                  </span>
                } @else {
                  <span
                    class="grid-cell-text text-green-600"
                    [attr.title]="tituloTabla(labels().estadoExitoso)"
                  >
                    <i class="pi pi-check mr-1"></i>{{ labels().estadoExitoso }}
                  </span>
                }
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="3" class="text-center text-gray-500 py-4">
                No hay filas para mostrar
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    </div>
  `
})
export class CargaMasivaResultadoComponent {
  resumen = input.required<CargaMasivaResumen>();
  tab = input.required<CargaMasivaTab>();
  onSetTab = input.required<(tab: CargaMasivaTab) => void>();
  tableConfig = input.required<CargaMasivaResultadoTableConfig>();
  labels = input.required<CargaMasivaResultadoLabels>();

  readonly filasVisibles = computed(() => {
    const detalles = this.resumen().detalles ?? [];
    switch (this.tab()) {
      case 'exitosos':
        return detalles.filter((fila) => fila.exitoso);
      case 'errores':
        return detalles.filter((fila) => !fila.exitoso);
      default:
        return detalles;
    }
  });

  readonly tableStyleClass = computed(() => {
    const config = this.tableConfig();
    const classes = ['p-datatable-sm', config.style.tableClassName];
    if (config.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (config.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  });

  setTab(tab: CargaMasivaTab): void {
    this.onSetTab()(tab);
  }

  principalValue(row: FilaResultado): string | number {
    return row.idRegistro ?? row.orden ?? '-';
  }

  errorValue(row: FilaResultado): string {
    return (
      row.mensaje ||
      (row.errores && row.errores.length > 0
        ? row.errores.join(', ')
        : 'Error desconocido')
    );
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }
}
